import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { GraduationCap, BookOpen, Trophy, Award, ArrowRight, Lock, Clock } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Academy = () => {
  const navigate = useNavigate();
  const [courses, setCourses] = useState([]);
  const [progress, setProgress] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return { Authorization: `Bearer ${token}` };
  };

  const fetchData = async () => {
    try {
      const [coursesRes, progressRes] = await Promise.all([
        axios.get(`${API}/academy/courses`),
        axios.get(`${API}/academy/progress`, { headers: getAuthHeaders() })
      ]);

      setCourses(coursesRes.data.courses);
      setProgress(progressRes.data);
    } catch (error) {
      console.error('Error fetching academy data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getCourseProgress = (courseId) => {
    const courseProgress = progress.courses?.find(c => c.course_id === courseId);
    return courseProgress?.progress || 0;
  };

  const getTierBadge = (tier) => {
    if (tier === 'free') return <Badge className="bg-green-500/20 text-green-300">FREE</Badge>;
    if (tier === 'pro') return <Badge className="bg-blue-500/20 text-blue-300">PRO</Badge>;
    if (tier === 'premium') return <Badge className="bg-purple-500/20 text-purple-300">PREMIUM</Badge>;
    return null;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#050505] via-[#0a0a0f] to-[#050505] text-white">
      {/* Header */}
      <nav className="glass border-b border-border sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-4 flex justify-between items-center">
          <div className="flex items-center gap-2">
            <h1 className="text-2xl font-bold">Bethereum Academy</h1>
            <Badge className="bg-yellow-500/20 text-yellow-300 border-yellow-500/30 text-xs">TESTNET</Badge>
          </div>
          <Button variant="ghost" onClick={() => navigate('/dashboard')}>Dashboard</Button>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-6 py-12">
        {/* Hero */}
        <div className="text-center mb-12">
          <div className="inline-block p-3 bg-indigo-600/10 rounded-full mb-4">
            <GraduationCap className="h-12 w-12 text-indigo-400" />
          </div>
          <h2 className="text-4xl md:text-5xl font-bold mb-4">Learn DeFi Step by Step</h2>
          <p className="text-xl text-gray-400 max-w-2xl mx-auto">
            Master blockchain and decentralized finance through hands-on practice
          </p>
        </div>

        {/* Quick Stats */}
        <div className="grid md:grid-cols-4 gap-4 mb-12">
          <Card className="bg-white/5 border-white/10">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-400">Courses</p>
                  <p className="text-2xl font-bold">{courses.length}</p>
                </div>
                <BookOpen className="h-8 w-8 text-indigo-400" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-white/5 border-white/10">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-400">Completed</p>
                  <p className="text-2xl font-bold">{progress.courses?.filter(c => c.progress >= 100).length || 0}</p>
                </div>
                <Award className="h-8 w-8 text-green-400" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-white/5 border-white/10">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-400">In Progress</p>
                  <p className="text-2xl font-bold">{progress.courses?.filter(c => c.progress > 0 && c.progress < 100).length || 0}</p>
                </div>
                <Clock className="h-8 w-8 text-yellow-400" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-white/5 border-white/10">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-400">Certificates</p>
                  <p className="text-2xl font-bold">{progress.courses?.filter(c => c.progress >= 100).length || 0}</p>
                </div>
                <Trophy className="h-8 w-8 text-purple-400" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Courses Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-2 gap-6">
          {courses.map((course) => {
            const courseProgress = getCourseProgress(course.id);
            const isLocked = course.tier_required !== 'free';

            return (
              <Card 
                key={course.id}
                className="bg-white/5 border-white/10 hover:border-indigo-500/50 transition-all cursor-pointer group"
                onClick={() => !isLocked && navigate(`/academy/courses/${course.id}`)}
              >
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <CardTitle className="text-xl">{course.title}</CardTitle>
                        {getTierBadge(course.tier_required)}
                      </div>
                      <CardDescription>{course.description}</CardDescription>
                    </div>
                    {isLocked && <Lock className="h-5 w-5 text-gray-400" />}
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center gap-4 text-sm text-gray-400">
                    <span>{course.lessons_count} lessons</span>
                    <span>•</span>
                    <span>{course.duration}</span>
                    <span>•</span>
                    <span>{course.level}</span>
                  </div>

                  {courseProgress > 0 && (
                    <div>
                      <div className="flex justify-between text-sm mb-2">
                        <span className="text-gray-400">Progress</span>
                        <span className="text-indigo-400 font-semibold">{Math.round(courseProgress)}%</span>
                      </div>
                      <Progress value={courseProgress} className="h-2" />
                    </div>
                  )}

                  <Button 
                    className="w-full group-hover:bg-indigo-600 transition-all"
                    disabled={isLocked}
                  >
                    {isLocked ? (
                      <><Lock className="h-4 w-4 mr-2" /> Upgrade to Unlock</>
                    ) : courseProgress > 0 ? (
                      <>Continue Learning <ArrowRight className="h-4 w-4 ml-2" /></>
                    ) : (
                      <>Start Course <ArrowRight className="h-4 w-4 ml-2" /></>
                    )}
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* CTA for Premium */}
        <Card className="mt-12 bg-gradient-to-r from-indigo-600/20 to-purple-600/20 border-indigo-500/30">
          <CardContent className="py-8 text-center">
            <Trophy className="h-12 w-12 text-indigo-400 mx-auto mb-4" />
            <h3 className="text-2xl font-bold mb-2">Unlock All Courses</h3>
            <p className="text-gray-300 mb-6">Upgrade to Pro or Premium to access advanced courses and earn certificates</p>
            <Button className="bg-indigo-600 hover:bg-indigo-700" onClick={() => navigate('/')}>View Plans</Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Academy;